import { Outlet, NavLink } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useEffect, useState } from "react";
import { adminApi } from "../services/api";
import { LanguageSwitcher } from "./i18n/LanguageSwitcher";
import { useI18n } from "@expertloop/i18n/react";
import { readAppVersion } from "../app/version";

export function DashboardLayout() {
	const { t } = useI18n();
	const admin = useAuthStore((s) => s.admin);
	const logout = useAuthStore((s) => s.logout);
	const [mediationCount, setMediationCount] = useState(0);
	const appVersion = readAppVersion();
	// Poll for mediation count every 30 seconds
	useEffect(() => {
		const fetchCount = async () => {
			try {
				const data = await adminApi.get<{ count: number }>("/admin/mediations/count");
				setMediationCount(data.count);
			} catch {
				// Ignore errors
			}
		};

		fetchCount();
		const interval = setInterval(fetchCount, 30000);
		return () => clearInterval(interval);
	}, []);

	const handleLogout = async () => {
		await logout();
	};

	return (
		<div className="dashboard-layout">
			<aside className="sidebar">
				<div className="sidebar-header">
					<h1>
						ExpertLoop
						<span className="badge">{t("adminAdmins.admin")}</span>
					</h1>
				</div>

				<nav className="sidebar-nav">
					<NavLink
						to="/"
						end
						className={({ isActive }) =>
							`nav-item ${isActive ? "active" : ""}`
						}
					>
						{t('adminNav.overview')}
					</NavLink>
					<NavLink
						to="/finance"
						className={({ isActive }) =>
							`nav-item ${isActive ? "active" : ""}`
						}
					>
						{t('adminNav.finance')}
					</NavLink>
					<NavLink
						to="/expert-referrals"
						className={({ isActive }) =>
							`nav-item ${isActive ? "active" : ""}`
						}
					>
						{t('adminNav.expertReferrals')}
					</NavLink>
					<NavLink
						to="/chats"
						className={({ isActive }) =>
							`nav-item ${isActive ? "active" : ""}`
						}
					>
						{t('adminNav.allChats')}
						{mediationCount > 0 && (
							<span className="count-badge">{mediationCount}</span>
						)}
					</NavLink>
					<NavLink
						to="/users"
						className={({ isActive }) =>
							`nav-item ${isActive ? "active" : ""}`
						}
					>
						{t('adminNav.users')}
					</NavLink>
					<NavLink
						to="/reports"
						className={({ isActive }) =>
							`nav-item ${isActive ? "active" : ""}`
						}
					>
						{t('adminNav.reports')}
					</NavLink>
					<NavLink
						to="/translations"
						className={({ isActive }) =>
							`nav-item ${isActive ? "active" : ""}`
						}
					>
						{t('adminNav.translations')}
					</NavLink>

					{/* Super Admin Only */}
					{admin?.role === "super_admin" && (
						<>
							<div className="nav-divider" />
							<NavLink
								to="/audit"
								className={({ isActive }) =>
									`nav-item ${isActive ? "active" : ""}`
								}
							>
								{t('adminNav.auditLog')}
							</NavLink>
							<NavLink
								to="/admins"
								className={({ isActive }) =>
									`nav-item ${isActive ? "active" : ""}`
								}
							>
								{t('adminNav.adminUsers')}
							</NavLink>
						</>
					)}

					<div className="nav-divider" />
					<NavLink
						to="/security"
						className={({ isActive }) =>
							`nav-item ${isActive ? "active" : ""}`
						}
					>
						{t('adminNav.securitySettings')}
					</NavLink>
				</nav>

				<div className="sidebar-footer">
					<LanguageSwitcher />
					<div className="admin-info">
						<div className="admin-avatar">
							{admin?.firstName?.[0]}
							{admin?.lastName?.[0]}
						</div>
						<div className="admin-details">
							<div className="admin-name">
								{admin?.firstName} {admin?.lastName}
							</div>
							<div className="admin-role">{admin?.role.replace("_", " ")}</div>
						</div>
					</div>
					<button className="btn-logout" onClick={handleLogout}>
						{t('adminNav.signOut')}
					</button>
					<p className="sidebar-version">v{appVersion}</p>
				</div>
			</aside>

			<main className="main-content">
				<Outlet />
			</main>
		</div>
	);
}
